import type { CaseId } from "./case.model";
import type { ConditionId } from "./condition.model";
import { InvalidTransitionError, PreconditionFailedError } from "./errors";
import type { DomainId } from "./lib/domain-id";
import { highWaterMark, type MemberId, type MemberIdSequence, memberIdSequence } from "./member-identity";
import type { PatientId } from "./patient.model";

/** Stable CarePlan identity, independent of its FHIR CarePlan locator. */
export type CarePlanId = DomainId<"CarePlanId">;

export function asCarePlanId(value: string): CarePlanId {
  if (!value) throw new PreconditionFailedError("A CarePlan identity cannot be empty");
  return value as CarePlanId;
}

// ============================================================
// CarePlan — the patient's treatment plan for a case (ADR-0017)
//
// Case-scoped and long-lived: it spans the whole course of care. Requested
// work lives in case-scoped OrderSets and treatment executions are standalone
// aggregates, so neither belongs inside this aggregate.
//
// OrderSets do not appear here. They are case-scoped aggregates with their own
// curation ordering; this plan owns conditions, goals, and its lifecycle.
// ============================================================

/** Every state a care plan can be in. The surface may not offer more than these. */
export const CARE_PLAN_STATUSES = ["draft", "active", "on-hold", "completed", "revoked"] as const;
export type CarePlanStatus = (typeof CARE_PLAN_STATUSES)[number];

export const GOAL_PRIORITIES = ["high-priority", "medium-priority", "low-priority"] as const;
export type GoalPriority = (typeof GOAL_PRIORITIES)[number];

export const GOAL_PROGRESS_VALUES = [
  "in-progress",
  "improving",
  "worsening",
  "no-change",
  "achieved",
  "sustaining",
  "not-achieved",
  "no-progress",
  "not-attainable",
] as const;
export type GoalProgress = (typeof GOAL_PROGRESS_VALUES)[number];

/** The target value: a measurable quantity or a free-text outcome. */
type GoalTarget =
  | {
      measure: string;
      due?: string;
      kind: "quantity";
      comparator?: "<" | "<=" | ">=" | ">";
      value: number;
      unit?: string;
    }
  | { measure: string; due?: string; kind: "text"; text: string };

export interface CarePlanGoal {
  /** Stable identity scoped to this CarePlan, independent of the Goal row locator. */
  id: MemberId;
  description: string;
  /**
   * Absent when the goal is not ranked. FHIR's `goal-priority` value set has
   * three codes and no "unranked" one, so absence IS the fourth state — the
   * read side has always spelled it that way, and a stored `none` code is a
   * legacy invention this reads as absent.
   */
  priority?: GoalPriority;
  progress: GoalProgress;
  /** Condition ids this goal targets — must be a subset of the plan's `addresses`. */
  addresses?: ConditionId[];
  startDate?: string;
  target?: GoalTarget;
  /** Set when the goal is no longer pursued. */
  retiredAt?: string;
}

export interface CarePlanProps {
  id: CarePlanId;
  status?: CarePlanStatus;
  patientId: PatientId;
  /** EpisodeOfCare id — the case this plan treats. */
  caseId?: CaseId;
  title?: string;
  description?: string;
  startedOn?: string;
  endedOn?: string;
  /** Condition ids the plan treats. Curated by the care manager, never copied wholesale from the case. */
  addresses: ConditionId[];
  goals: CarePlanGoal[];
}

export interface CreateCarePlanInput extends Omit<CarePlanProps, "goals" | "status"> {
  goals: Array<Omit<CarePlanGoal, "id">>;
}

/**
 * What persistence can actually hand back. FHIR's `Goal.achievementStatus` is
 * optional, and a goal stored without one is not a goal with no progress — it
 * is one nobody has reported on yet, which is what `in-progress` means. The
 * default is restoration's to apply, so storage is free to say nothing.
 */
export type StoredCarePlanGoal = Omit<CarePlanGoal, "progress"> & { progress?: GoalProgress };
export type StoredCarePlanProps = Omit<CarePlanProps, "goals"> & { goals: StoredCarePlanGoal[] };

type CarePlanEvent = "HOLD" | "RESUME" | "COMPLETE" | "REVOKE";

const TRANSITIONS: Record<CarePlanStatus, Partial<Record<CarePlanEvent, CarePlanStatus>>> = {
  draft: { REVOKE: "revoked" },
  active: { HOLD: "on-hold", COMPLETE: "completed", REVOKE: "revoked" },
  "on-hold": { RESUME: "active", REVOKE: "revoked" },
  completed: {},
  revoked: {},
};

export class CarePlan {
  private state: Required<Pick<CarePlanProps, "status">> & CarePlanProps;
  private readonly goalIds: MemberIdSequence;

  private constructor(props: CarePlanProps) {
    if (!props.patientId) {
      throw new PreconditionFailedError("Care plan requires a patient");
    }
    for (const goal of props.goals) {
      if (!goal.description) {
        throw new PreconditionFailedError("Every goal requires a description");
      }
      for (const conditionId of goal.addresses ?? []) {
        if (!props.addresses.includes(conditionId)) {
          throw new PreconditionFailedError(`Goal targets condition '${conditionId}', which the plan does not address`);
        }
      }
    }
    this.state = structuredClone({ ...props, status: props.status ?? "draft" });
    this.goalIds = memberIdSequence("goal", highWaterMark(props.goals));
  }

  static create(input: CreateCarePlanInput): CarePlan {
    const sequence = memberIdSequence("goal");
    return new CarePlan({
      ...structuredClone(input),
      status: "active",
      id: asCarePlanId(input.id),
      goals: input.goals.map((goal) => ({ ...structuredClone(goal), id: sequence.next() })),
    });
  }

  /** What a goal nobody has reported on is making, and what storage may leave unsaid. */
  static readonly UNREPORTED_PROGRESS: GoalProgress = "in-progress";

  static restore(snapshot: StoredCarePlanProps): CarePlan {
    return new CarePlan({
      ...snapshot,
      goals: snapshot.goals.map((goal) => ({ ...goal, progress: goal.progress ?? CarePlan.UNREPORTED_PROGRESS })),
    });
  }

  // ── facts ──

  get id(): CarePlanId {
    return this.state.id;
  }

  get status(): CarePlanStatus {
    return this.state.status;
  }

  get patientId(): PatientId {
    return this.state.patientId;
  }

  /** The episode this plan belongs to. Absent on a plan written outside any case. */
  get caseId(): CaseId | undefined {
    return this.state.caseId;
  }

  get addresses(): readonly ConditionId[] {
    return [...this.state.addresses];
  }

  get goals(): readonly CarePlanGoal[] {
    return structuredClone(this.state.goals);
  }

  /** Whether the plan is being worked — what other aggregates ask before acting on it. */
  get isActive(): boolean {
    return this.state.status === "active";
  }

  get isDraft(): boolean {
    return this.state.status === "draft";
  }

  snapshot(): CarePlanProps {
    return structuredClone(this.state);
  }

  // ── lifecycle ──

  hold(): void {
    this.state.status = this.nextStatus("HOLD");
  }

  resume(): void {
    this.state.status = this.nextStatus("RESUME");
  }

  complete(now: Date): void {
    const next = this.nextStatus("COMPLETE");
    this.state.status = next;
    this.state.endedOn = now.toISOString();
  }

  revoke(now: Date): void {
    const next = this.nextStatus("REVOKE");
    this.state.status = next;
    this.state.endedOn = now.toISOString();
  }

  // ── what the plan says it is ──

  /**
   * Re-word the plan. Only what a care manager types: everything else is
   * reached through the operation that owns it, so this cannot become the back
   * door that moves a plan to another patient or rewrites the visits it plans.
   *
   * The exclusion is stripped at RUNTIME, not just in the type — a caller with
   * `any` in hand (a tRPC body, a test) would otherwise walk straight past the
   * compiler and `Object.assign` would take the lot.
   */
  revise(changes: Partial<Pick<CarePlanProps, "title" | "description">>): void {
    this.assertMutable();
    const { title, description } = changes;
    if (title !== undefined) this.state.title = title;
    if (description !== undefined) this.state.description = description;
  }

  // ── what the plan treats ──

  /** Curate the conditions this plan treats. Dropping one that a goal targets is refused. */
  setAddresses(conditionIds: ConditionId[]): void {
    this.assertMutable();
    for (const goal of this.state.goals) {
      for (const targeted of goal.addresses ?? []) {
        if (!conditionIds.includes(targeted)) {
          throw new PreconditionFailedError(
            `Cannot stop addressing condition '${targeted}' while a goal still targets it`,
          );
        }
      }
    }
    this.state.addresses = [...conditionIds];
  }

  // ── goals ──

  addGoal(goal: Omit<CarePlanGoal, "id" | "progress"> & { progress?: GoalProgress }): void {
    this.assertMutable();
    if (!goal.description) {
      throw new PreconditionFailedError("Every goal requires a description");
    }
    for (const conditionId of goal.addresses ?? []) {
      if (!this.state.addresses.includes(conditionId)) {
        throw new PreconditionFailedError(`Goal targets condition '${conditionId}', which the plan does not address`);
      }
    }
    this.state.goals.push({
      ...goal,
      id: this.goalIds.next(),
      progress: goal.progress ?? CarePlan.UNREPORTED_PROGRESS,
    });
  }

  /**
   * `addresses` is revisable because a goal can be re-aimed, and it is checked
   * against the plan for the same reason `addGoal` checks it: a goal may only
   * target a condition the plan treats. It was previously absent from this
   * type while the goal form sent it anyway, so it wrote through unvalidated.
   */
  reviseGoal(
    goalId: MemberId,
    changes: Partial<Pick<CarePlanGoal, "description" | "priority" | "target" | "startDate" | "addresses">>,
  ): void {
    this.assertMutable();
    const goal = this.state.goals.find((g) => g.id === goalId);
    if (!goal) throw new PreconditionFailedError(`Goal '${goalId}' is not on this plan`);
    if (changes.description !== undefined && !changes.description) {
      throw new PreconditionFailedError("A goal cannot be revised to have no description");
    }
    for (const conditionId of changes.addresses ?? []) {
      if (!this.state.addresses.includes(conditionId)) {
        throw new PreconditionFailedError(`Goal targets condition '${conditionId}', which the plan does not address`);
      }
    }
    Object.assign(goal, changes);
  }

  // ── internals ──

  private nextStatus(event: CarePlanEvent): CarePlanStatus {
    const target = TRANSITIONS[this.state.status]?.[event];
    if (!target) throw new InvalidTransitionError(this.state.status, event);
    return target;
  }

  /**
   * A plan that is over is a record, not a working document. The lifecycle
   * transitions have their own table and do not go through here — this guards
   * the CONTENT, which nothing may rewrite after the fact.
   */
  private assertMutable(): void {
    if (this.state.status === "completed" || this.state.status === "revoked") {
      throw new PreconditionFailedError(`Cannot modify a care plan in '${this.state.status}' status`);
    }
  }
}
